import type { Mesh, Object3D, Sprite } from "three";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import type { Engine } from "../../../engine/Engine";
import { shouldExcludeFromAo } from "../../../engine/postprocesses/aoVisibility";
import { isOutlineTslEffect } from "./tsl/TslEffects";

function isExcludedFromOutline(object: Object3D): boolean {
  let current: Object3D | null = object;
  while (current) {
    if (shouldExcludeFromAo(current)) return true;
    current = current.parent;
  }
  return false;
}

/** Product meshes only — bbox meters / replace boxes stay out of the edge mask. */
function collectOutlineTargets(objects: Object3D[]): Object3D[] {
  const targets: Object3D[] = [];
  const seen = new Set<Object3D>();
  for (const root of objects) {
    if (isExcludedFromOutline(root)) continue;
    root.traverse((child) => {
      if (isExcludedFromOutline(child)) return;
      if ((child as Sprite).isSprite) return;
      if (!(child as Mesh).isMesh) return;
      if (seen.has(child)) return;
      seen.add(child);
      targets.push(child);
    });
  }
  return targets;
}

/** Sets outline selection on registered TSL outline effect and/or classic OutlinePass. */
export function setOutlineSelectedObjects(
  engine: Engine | undefined,
  objects: Object3D[],
): void {
  if (!engine) return;

  const outlineObjects = collectOutlineTargets(objects);

  const tslOutline = engine.findEffect(isOutlineTslEffect);
  if (tslOutline) {
    const graphChanged = tslOutline.setSelectedObjects(outlineObjects);
    if (graphChanged) engine.invalidateTslEffects();
    engine.dispatchCustomEvent("request-update");
    return;
  }

  engine.mutatePassComposerIfExists((composer) => {
    const pass = composer.passes.find(
      (p): p is OutlinePass => p instanceof OutlinePass,
    );
    if (pass) pass.selectedObjects = outlineObjects;
  });
}
